import { CellProps } from 'react-table'
import { formatDateTime, IconPaths, ModalSize } from '../../../../../_metronic/helpers'
import {
  CustomTable,
  PlainHeader,
  RowActions,
  StackedCell,
  TableColumn,
  TableEmptyState,
  useModal,
} from '../../../../components'
import { RatcRegistro, SituacaoAnexoIi } from '../core/_models'
import { getRatcDoAtendimento } from '../core/_requests'
import { formatarHoras, ROTULO_SITUACAO_ANEXO_II } from '../core/filtro'
import { useParametrosRelatorios } from '../core/ListFiltersProvider'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { RatcDetalheModal } from '../secoes/RatcDetalheModal'

const RatcActionsCell = ({ registro }: { registro: RatcRegistro }) => {
  const { showModal } = useModal()
  const identificador = registro.teleconsultoria?.identificador ?? String(registro.id)

  const verRegistro = () =>
    showModal({
      title: `RATC — ${identificador}`,
      content: <RatcDetalheModal />,
      size: ModalSize.Large,
      fetchers: { ratc: () => getRatcDoAtendimento(registro.id) },
    })

  return (
    <RowActions
      itemName={`interconsulta ${identificador}`}
      primaryAction={{ label: 'Ver registro', onClick: verRegistro, icon: IconPaths.Search }}
    />
  )
}

const columns: ReadonlyArray<TableColumn<RatcRegistro>> = [
  {
    Header: () => <PlainHeader>Identificador</PlainHeader>,
    id: 'identificador',
    cellWidth: '200px',
    Cell: ({ row }: CellProps<RatcRegistro>) => (
      <StackedCell
        tabular
        primary={row.original.teleconsultoria?.identificador ?? row.original.id}
        secondary={
          row.original.teleconsultoria?.abertura
            ? formatDateTime(row.original.teleconsultoria.abertura)
            : undefined
        }
      />
    ),
  },
  {
    Header: () => <PlainHeader>Especialidade</PlainHeader>,
    id: 'especialidade',
    Cell: ({ row }: CellProps<RatcRegistro>) => (
      <StackedCell primary={row.original.teleconsultoria?.especialidade} />
    ),
  },
  {
    Header: () => <PlainHeader>Solicitante</PlainHeader>,
    id: 'solicitante',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<RatcRegistro>) => (
      <StackedCell
        primary={row.original.solicitante?.nome}
        secondary={row.original.solicitante?.estabelecimento}
      />
    ),
  },
  {
    Header: () => <PlainHeader>Situação (Anexo II)</PlainHeader>,
    id: 'situacaoAnexoIi',
    cellWidth: '170px',
    Cell: ({ row }: CellProps<RatcRegistro>) => {
      const situacao = row.original.teleconsultoria?.situacaoAnexoIi as SituacaoAnexoIi | undefined
      return <StackedCell primary={situacao && ROTULO_SITUACAO_ANEXO_II[situacao]} />
    },
  },
  {
    Header: () => <PlainHeader>Tempo de resposta</PlainHeader>,
    id: 'horasResposta',
    cellWidth: '160px',
    align: 'end',
    Cell: ({ row }: CellProps<RatcRegistro>) => (
      <StackedCell tabular primary={formatarHoras(row.original.teleconsultoria?.horasResposta)} />
    ),
  },
  {
    Header: () => <PlainHeader>Ações</PlainHeader>,
    id: 'actions',
    cellWidth: '96px',
    align: 'end',
    Cell: ({ row }: CellProps<RatcRegistro>) => <RatcActionsCell registro={row.original} />,
  },
]

/** Registros do RATC no período, paginados pelo backend (prévia; o arquivo completo é a exportação). */
export const RatcTable = () => {
  const { periodo } = useParametrosRelatorios()
  const { isLoading, isError, refetch, response } = useQueryResponse()

  return (
    <CustomTable<RatcRegistro>
      id='kt_table_relatorio_ic_ratc'
      ariaLabel='Registros do RATC'
      itemLabel={{ singular: 'registro', plural: 'registros' }}
      data={response?.data ?? []}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      queryHooks={{ useQueryRequest, useQueryResponse }}
      emptyState={
        periodo ? (
          <TableEmptyState
            title='Nenhuma interconsulta no período'
            description='Revise o período e as unidades escolhidas.'
          />
        ) : (
          <TableEmptyState
            title='Escolha a empresa e o período'
            description='O relatório aparece com a empresa e um período válido (até 366 dias).'
          />
        )
      }
    />
  )
}
